import { StopOutlined } from "@ant-design/icons"
import { ReasonForChange } from "./types"

const PRODUCTION = process.env.NODE_ENV === "production"

export const DEFAULT_LANGUAGE = "en"
const PRODUCTION_LANGUAGES = [
  "da-dk",
  "de-de",
  "pl-pl",
  "sv-se",
  "tr-tr",
  "es-cl",
  "es-es",
  "zh-cn",
  "nl-nl",
]
const BETA_LANGUAGES = [
  "da-dk",
  "de-de",
  "pl-pl",
  "sv-se",
  "tr-tr",
  "es-cl",
  "es-es",
  "zh-cn",
  "nl-nl",
]

/** 🇬🇧 , 🇩🇰 , 🇵🇱  */
export const SUPPORTED_LANGUAGES = Object.freeze(
  [DEFAULT_LANGUAGE].concat(PRODUCTION ? PRODUCTION_LANGUAGES : BETA_LANGUAGES)
)

export const FRONTPAGE = "processes"

export const EMPTY_OPF_ITEM = Object.freeze({ id: null, slug: null, opfId: null, level: 1 })
export const EMPTY_HTML = "<p><br></p>"
export const NULL_DATE = "0001-01-01T00:00:00"

/** Application initialization status types, used to identify boot progress. */
export const INIT_STATUS = Object.freeze({
  FAILED: "failed",
  FINISHED: "finished",
  WAITING: "waiting",
  LOADING: "loading",
  NO_CLAIMS: "noClaims",
})

/** Policy Hub Hierarchy trees used in ApplicationContext */
export const TREES = Object.freeze({
  /** Hierarchy for Documents with Policies and Standards */
  POLICIES_STANDARDS: "policies-standards",
  /** Hierarchy for Policy Hub Items */
  PROCESSES: "processes",
  /** Hierarchy for Documents with Commercial Legal */
  COMMERCIAL_LEGAL: "commercial-legal",
  /** Hierarchy for Certifications */
  CERTIFICATIONS: "certifications",
})

/** Actions that can be performed on Policy Hub data */
export const ACTIONS = Object.freeze({
  ADD: "add",
  ADD_PROCEDURE: "add procedure",
  EDIT: "edit",
  ARCHIVE: "archive",
  REMOVE_FROM_CONFIGURATOR: "removeFromConfigurator",
  REFINER: "refiner",
  CREATE: "create",
  LOCK: "lock",
  MOVE: "move",
  UNLOCK: "unlock",
  TRANSLATE: "translate",
  UPDATE_OWNERS: "updateOwner",
  UPDATE_ORDER: "updateOrder",
  ADD_FAVORITE: "addToFavorites",
  REMOVE_FAVORITE: "removeFromFavorites",
  UPLOAD_DOCUMENT: "uploadDocument",
  VIEWABLE_BY: "viewableBy",
  ADD_STEP: "addStep",
  EDIT_STEP: "editStep",
  DELETE_STEP: "deleteStep",
  UNDEFINED_ACTION: "undefinedAction",
  ADD_DOCUMENT_CATEGORY: "addDocumentCategory",
  ADD_DOCUMENT_SUBCATEGORY: "addDocumentSubcategory",
  EDIT_TOOLS: "editTools",
  TRANSLATE_TOOLS: "translateTools",
  EDIT_RELATIONS: "editRelations",
  UPLOAD_PDF: "uploadPdf",
  CONFIGURE_PROCESSES: "configureProcesses",
  CONFIGURE_POLICIES_STANDARS: "configurePoliciesStandards",
  CONFIGURE_COMMERCIAL_LEGAL: "configureCommercialLegal",
  ADD_CHAPTER: "addChapter",
  SET_NOTIFICATION: "setNotification",
  SET_PUBLISH_VERSION: "setPublishVersion",
  PRINT: "print",
  PREPUBLISH: "prePublish",
  PUBLISH: "publish",
  EDIT_LOCAL_AMENDMENT: "editLocalAmendments",
  START_DOCUMENT_REVIEW: "startReview",
  STOP_DOCUMENT_REVIEW: "stopReview",
  COMPARE: "compare",
})

export const ANALYTICS = Object.freeze({
  EVENT_VPV: "vpv",
  EVENT_ACTION: "action",
  EVENT_CATEGORY_TOP_LEVEL_EVENTS: "top level events",
  EVENT_CATEGORY_SECOND_LEVEL_EVENTS: "second level events",
  INTERACTION_EVENT: "interactionevent",
  COMPARE_VIEW: "new compare view",
  ITEM_ACTIONS: "item actions",
  POLICY_STANDARD_ACTIONS: "policiesStandards",
  ACCESSING_RESOURCES: "accessing resources",
  START_ADD: "Add - Start",
  END_ADD: "Add - Done",
  START_EDIT: "Edit - Start",
  END_EDIT: "Edit - Done",
  // START_CREATE_VARIANT: "Create Variant - Start",
  // END_CREATE_VARIANT: "Create Variant - Done",
})

export const ROLES = Object.freeze({
  SYS_ADMIN: "System Administrator",
  GLOBAL_PROCESS_OWNER: "Global Process Owner",
  GLOBAL_POLICIES_STANDARDS_OWNER: "Global Policies-Standards Owner",
  GLOBAL_ACCOUNT_PROCESS_OWNER: "Global Account Process Owner",
  COUNTRY_PROCESS_OWNER: "Country Process Owner",
  ACCOUNT_MANAGER: "Account Manager",
  SITE_MANAGER: "Site Process Owner",
  LOCAL_MANAGER: "Local Manager",
  SERVICE_EMPLOYEE: "Service Employees",
  ANONYMOUS: "Anonymous",
})

export const OPF_ITEM_TYPES = Object.freeze({
  PROCEDURE_ELEMENT: "procedure element",
  PROCEDURE_CATEGORY: "procedure category",
  PROCEDURE: "procedure",
  PROCEDURE_STEP: "procedure step",
  PROCESS: "process",
  PROCESS_ELEMENT: "process element",
  PROCESS_CATEGORY: "process category",
  CONFIGURATOR: "configurator",
  WORD_IMPORT: "word import",
})

export const OPF_SCOPES = Object.freeze({
  GLOBAL: "global",
  LOCAL: "local",
  // GLOBAL_VARIANT: "global variant",
  // LOCAL_VARIANT: "local variant",
})

export const OPF_HEADER_HEIGHT_PX = 90

/** Configuration types for Policy Hub */
export const OPF_CONFIGURATION_TYPES = Object.freeze({
  ACCOUNT: "account",
  GLOBAL_ACCOUNT: "globalaccount",
  COUNTRY: "country",
  COUNTRY_ACCOUNT: "countryaccount",
  SITE: "site",
})

export const OPF_DOCUMENT_TYPES = Object.freeze({
  DOCUMENT_CATEGORY: "document category",
  DOCUMENT_SUBCATEGORY: "document subcategory",
  DOCUMENT_CATEGORY_VERSION: "document category version",
  DOCUMENT_SUBCATEGORY_VERSION: "document subcategory version",
  DOCUMENT: "document",
  POLICY: "policy",
  POLICY_VERSION: "policy version",
  STANDARD: "standard",
  STANDARD_VERSION: "standard version",
  POLICIES_STANDARDS_CATEGORY: "policies-standards category",
  POLICIES_STANDARDS_SUBCATEGORY: "policies-standards subcategory",
  POLICIES_STANDARDS_CATEGORY_VERSION: "policies-standards category version",
  POLICIES_STANDARDS_SUBCATEGORY_VERSION: "policies-standards subcategory version",
  CERTIFICATION: "certification",
  CERTIFICATION_CATEGORY: "certification category",
  CERTIFICATION_SUBCATEGORY: "certification subcategory",
})

type Classification = {
  label: string
  description: string
  longdescription: string
  icon: React.ReactNode
  viewlabel: string
}

export const CLASSIFICATIONS: Record<number, Classification> = {
  0: {
    label: "common.classification.selectone",
    description: "common.classification.selectone.description",
    longdescription: "common.classification.selectone.longdescription",
    icon: null,
    viewlabel: "common.classification.empty",
  },
  1: {
    label: "common.confidential",
    description: "common.confidential.description",
    longdescription: "common.confidential.longdescription",
    icon: <StopOutlined />,
    viewlabel: "common.confidential",
  },
  2: {
    label: "common.restrictedExternal",
    description: "common.restrictedExternal.description",
    longdescription: "common.restrictedExternal.longdescription",
    icon: <StopOutlined />,
    viewlabel: "common.restrictedExternal",
  },
  3: {
    label: "common.restrictedInternal",
    description: "common.restrictedInternal.description",
    longdescription: "common.restrictedInternal.longdescription",
    icon: <StopOutlined />,
    viewlabel: "common.restrictedInternal",
  },
  4: {
    label: "common.unrestricted",
    description: "common.unrestricted.description",
    longdescription: "common.unrestricted.longdescription",
    icon: null,
    viewlabel: "common.unrestricted",
  },
}

export const REASON_FOR_CHANGE_OPTIONS: { value: ReasonForChange; label: string }[] = [
  { value: ReasonForChange.NEW_DOCUMENT, label: "common.newDocument" },
  { value: ReasonForChange.PERIODICAL_REVIEW, label: "common.periodicalReview" },
  {
    value: ReasonForChange.REQUIREMENT_CHANGE_DO_RADDED,
    label: "common.requirementChangedOrAdded",
  },
  { value: ReasonForChange.PROCESS_CHANGED, label: "common.processChanged" },
  { value: ReasonForChange.ACCOUNTABILITY_CHANGED, label: "common.accountabilityChanged" },
  { value: ReasonForChange.SIGNIFICANT_CHANGE_IN_DATA, label: "common.significantChangeInData" },
  { value: ReasonForChange.NEW_LEGAL_REQUIREMENT, label: "common.newLegalRequirement" },
  { value: ReasonForChange.AUDIT_RECOMMENDATIONS, label: "common.auditRecommendations" },
  { value: ReasonForChange.DOCUMENT_RETIREMENT, label: "common.documentRetirement" },
]

export enum ChangeType {
  DOCUMENT = "document",
  SUPPORTING_DOCUMENT = "supportingDocument",
  CORRECTION = "correction",
}

export type ChangedDocument = {
  label: string
  radiovalue: string
  inchangelog: boolean
  innotifications: boolean
}

export const CHANGED_DOCUMENT_OPTIONS_PROCESSES: Record<number, ChangedDocument> = {
  1: {
    label: "form.notification.changeTo.document",
    radiovalue: ChangeType.DOCUMENT,
    inchangelog: true,
    innotifications: true,
  },
  2: {
    label: "form.notification.changeTo.supportingDocument",
    radiovalue: ChangeType.SUPPORTING_DOCUMENT,
    inchangelog: true,
    innotifications: true,
  },
  3: {
    label: "form.notification.changeTo.correction",
    radiovalue: ChangeType.CORRECTION,
    inchangelog: false,
    innotifications: false,
  },
}

export const CHANGED_DOCUMENT_OPTIONS: Record<number, ChangedDocument> = {
  1: {
    label: "form.notification.changeTo.document",
    radiovalue: ChangeType.DOCUMENT,
    inchangelog: true,
    innotifications: true,
  },
  3: {
    label: "form.notification.changeTo.supportingDocument",
    radiovalue: ChangeType.SUPPORTING_DOCUMENT,
    inchangelog: false,
    innotifications: true,
  },
  4: {
    label: "form.notification.changeTo.correction",
    radiovalue: ChangeType.CORRECTION,
    inchangelog: false,
    innotifications: false,
  },
}

export const EDITING_FORM = "editing"
export const TRANSLATING_FORM = "translating"
export const PUBLISHING_FORM = "publishing"
